import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [inputValue, setInputValue] = useState(String(currentPage + 1));

  useEffect(() => {
    setInputValue(String(currentPage + 1));
  }, [currentPage]);

  if (totalPages <= 1) return null;

  const commitInputValue = () => {
    const parsed = parseInt(inputValue, 10);
    if (Number.isNaN(parsed)) {
      setInputValue(String(currentPage + 1));
      return;
    }
    const clamped = Math.min(totalPages, Math.max(1, parsed));
    setInputValue(String(clamped));
    onPageChange(clamped - 1);
  };

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={() => onPageChange(Math.max(0, currentPage - 1))}
        disabled={currentPage === 0}
        className="p-2 rounded-lg border border-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
      >
        <ChevronLeft size={16} className="text-dark" />
      </button>

      <div className="flex items-center gap-1 mx-2">
        <input
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={commitInputValue}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitInputValue();
              e.currentTarget.blur();
            }
          }}
          className="w-10 text-sm font-medium text-dark text-center border border-neutral-200 rounded-md py-0.5 focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="Page"
        />
        <span className="text-sm text-secondary">/</span>
        <span className="text-sm text-secondary">{totalPages}</span>
      </div>

      <button
        onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
        disabled={currentPage >= totalPages - 1}
        className="p-2 rounded-lg border border-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
      >
        <ChevronRight size={16} className="text-dark" />
      </button>
    </div>
  );
};
